import React, { useCallback, useEffect } from "react";
import { Card, Typography, Box, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { LoadingOverlayWithSpinner } from "components/common/LoadingOverlay";
import {
  selectAnalyticsEndDate,
  selectAnalyticsStartDate,
  selectProductAnalyticsData,
  selectProductFetched,
  selectIsProductChildrenFetched,
  selectIsProductChildrenFetching,
  selectCategoryByProductId,
  selectFailedCategoryProductIds,
} from "store/product/ProductSelectors";
import {
  analyticsDataLoader,
  loadSingleProductAnalytics,
  resetSingleProducAnalyticView,
  loadCategoryByProductId,
} from "store/product/ProductActions";
import AppState from "store/AppState";
import AnalyticsOverlay from "components/overlays/AnalyticsOverlay";
import { KPI_PRODUCT_METRIC_LABELS } from "utils/Constants";
import { formatMetricValue } from "utils/PerformanceReportUtils";
import { getMultiMetricDisplay } from "utils/KpiConfigUtils";
import { MetricType } from "./PerformanceTypes";
import { useProductImage } from "hooks/useProductImage";
import { refreshProductFromList } from "store/product-list/ProductListActions";
import { selectCurrentCatalogId } from "store/catalog/CatalogSelectors";
import { selectCurrentLocale } from "store/store-list/StoreListSelectors";
import { selectPerformanceReportStoreId } from "store/performance-report/PerformanceReportSelectors";
import ProductActionIcons from "components/common/ProductActionIcons";
import { selectIsAnalyticsEnabled } from "store/adapter-config/AdapterConfigSelectors";
import ProductCategoryOverlay from "components/overlays/ProductCategoryOverlay";
import { useHasEntitlement } from "hooks/useEntitlement";
import { useCurrencySymbol } from "hooks/useCurrencySymbol";

const ProductCard = styled(Card)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-start",
  flexDirection: "column",
  alignItems: "center",
  width: "252px",
  height: "390px",
  padding: "4px",
  position: "relative",
  border: `1px solid ${theme.palette.neutral.ashGrey}`,
  background: "#FFF",
}));

const ImageContainer = styled(Box)(() => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  overflow: "hidden",
  margin: "auto",
  width: "170px",
  height: "230px",
  paddingTop: "15px",
  "& .image": {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
}));

const EmptyImageContainer = styled(Box)(() => ({
  height: "230px",
  width: "170px",
  background: "whitesmoke",
  margin: "auto",
  paddingTop: "15px",
}));

const ProductInfoContainer = styled(Box)(() => ({
  width: "200px",
  height: "59px",
  margin: "auto",
  marginTop: "10px",
}));

const ProductName = styled(Typography)(({ theme }) => ({
  textAlign: "center",
  overflow: "hidden",
  textOverflow: "ellipsis",
  direction: "ltr",
  whiteSpace: "nowrap",
  maxWidth: "100%",
}));

const ProductDetail = styled(Typography)(({ theme }) => ({
  textAlign: "center",
  color: theme.palette.common.black,
  fontSize: "12px",
  fontWeight: 400,
  overflow: "hidden",
  textOverflow: "ellipsis",
  direction: "ltr",
  whiteSpace: "nowrap",
  maxWidth: "100%",
  lineHeight: 1.43,
}));

const StockContainer = styled(Box)(() => ({
  marginTop: "10px",
  marginLeft: "15px",
  height: "48px",
}));

const ProductContainer = styled(Box)(() => ({
  position: "relative",
}));

const ProductContent = styled(Box)(() => ({
  width: "100%",
}));

const MetricContainer = styled(Box)(() => ({
  marginLeft: "15px",
}));

const StyledBox = styled(Box)(() => ({
  height: "17.16px",
}));

interface ProductItemComponentProps {
  index: number;
  product?: any;
  code: string;
  price?: number;
  productId: string;
  productMetricValue: number;
  productMetricValues?: number[];
  selectedView: string;
  metricType: MetricType;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  isCategoriesDisplayed?: boolean;
  setIsCategoriesDisplayed?: (value: boolean) => void;
  displayMetrics?: Array<{ metricId: string; label: string }>;
  metricIds?: string[];
}

const ProductItemComponent: React.FC<ProductItemComponentProps> = ({
  index,
  product,
  code,
  price,
  productId,
  productMetricValue,
  productMetricValues,
  selectedView,
  metricType,
  dateRange,
  isCategoriesDisplayed,
  setIsCategoriesDisplayed,
  displayMetrics,
  metricIds,
}) => {
  const currencySymbol = useCurrencySymbol(product?.currencyCode);
  const dispatch = useDispatch();
  const intl = useIntl();

  const startDate = useSelector(selectAnalyticsStartDate);
  const endDate = useSelector(selectAnalyticsEndDate);
  const catalogId = useSelector(selectCurrentCatalogId);
  const performanceStoreId = useSelector(selectPerformanceReportStoreId);
  const localeCode = useSelector(selectCurrentLocale);
  const isAnalyticsEnabled = useSelector((state: AppState) =>
    selectIsAnalyticsEnabled(state),
  );

  const productAnalyticsId = product?.analyticsId ?? code;
  const showProductAnalytics = useSelector((state: AppState) =>
    selectProductAnalyticsData(state, productAnalyticsId),
  )?.showAnalytics;
  const loadSingleProduct = useSelector((state: AppState) =>
    selectProductAnalyticsData(state, productAnalyticsId),
  )?.loadAnalytics;
  const skuCoverage = product?.skuCoverage;
  const productDetails = useSelector((state: AppState) =>
    selectProductFetched(state, productId),
  );
  const isRefreshing = productDetails?.isRefreshing || false;
  const isProductChildrenFetched = useSelector(selectIsProductChildrenFetched);
  const isProductChildrenFetching = useSelector(
    selectIsProductChildrenFetching,
  );
  const categoryList = useSelector((state: AppState) =>
    selectCategoryByProductId(state, productId),
  );
  const failedCategoryProductIds = useSelector(selectFailedCategoryProductIds);
  const hasCatalogEntitlement = useHasEntitlement("catalog");

  // Set isCategoriesDisplayed to false if the category fetch failed
  useEffect(() => {
    if (failedCategoryProductIds.includes(productId) && isCategoriesDisplayed) {
      setIsCategoriesDisplayed?.(false);
    }
  }, [
    failedCategoryProductIds,
    productId,
    isCategoriesDisplayed,
    setIsCategoriesDisplayed,
  ]);

  const isMultiMetricView = displayMetrics && displayMetrics.length > 1;
  const productMetricLabel =
    displayMetrics && displayMetrics.length === 1
      ? displayMetrics[0].label
      : (KPI_PRODUCT_METRIC_LABELS.find(
          (metric) => metric.label === selectedView,
        )?.value ?? "Views");

  const handleProductAnalytics = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    if (isCategoriesDisplayed) {
      setIsCategoriesDisplayed?.(false);
    }
    if (!showProductAnalytics) {
      const fromDate = dateRange.startDate;
      const toDate = dateRange.endDate;
      if (!fromDate || !toDate) {
        return;
      }
      dispatch(analyticsDataLoader(productAnalyticsId));
      if (fromDate !== startDate || toDate !== endDate) {
        const analyticsPayload = {
          startDate: fromDate,
          endDate: toDate,
          productCodes: [productAnalyticsId],
          isFromPerformance: true,
        };
        dispatch(loadSingleProductAnalytics(analyticsPayload));
      }
    } else {
      dispatch(resetSingleProducAnalyticView(productAnalyticsId));
    }
  };

  const handleRefreshProduct = useCallback(
    (event) => {
      console.log("storeId", performanceStoreId);
      event.stopPropagation();
      let payload = {
        productId,
        catalogId,
        localeCode,
        storeId: performanceStoreId,
      };
      dispatch(refreshProductFromList(payload));
    },
    [catalogId, localeCode, performanceStoreId, dispatch, productId],
  );

  const handleCategories = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    if (!isCategoriesDisplayed) {
      if (showProductAnalytics) {
        dispatch(resetSingleProducAnalyticView(productAnalyticsId));
      }
      setIsCategoriesDisplayed?.(true);
      const payload = {
        productIds: [productId],
        storeId: performanceStoreId || undefined,
      };
      dispatch(loadCategoryByProductId(payload));
    } else {
      setIsCategoriesDisplayed?.(false);
    }
  };

  const productImage = useProductImage(productId, product);

  const calculatePrice = price
    ? `${currencySymbol}${price}`
    : product?.price
      ? `${currencySymbol}${product.price}`
      : "";

  return (
    <ProductContainer>
      <ProductCard variant="outlined">
        {isRefreshing && <LoadingOverlayWithSpinner />}
        {loadSingleProduct || showProductAnalytics ? (
          <AnalyticsOverlay
            productId={productId}
            showProductAnalytics={showProductAnalytics}
            loadSingleProductAnalytics={loadSingleProduct}
            isSubstituteOverlay={true}
            isPerformance={true}
          />
        ) : null}
        {isCategoriesDisplayed &&
          hasCatalogEntitlement &&
          (isProductChildrenFetching ||
            isProductChildrenFetched ||
            categoryList.length > 0) && (
            <ProductCategoryOverlay
              productId={productId}
              showOverlay={true}
              isSubstituteOverlay={true}
            />
          )}
        <ProductContent>
          <StockContainer>
            <Box>
              <StyledBox>
                <Typography variant="title2">{`#${index}`}</Typography>
              </StyledBox>
              <StyledBox>
                <Typography variant="gridTitle">
                  {intl.formatMessage({
                    id: "productDetail.inventory",
                    defaultMessage: "INV:",
                  })}{" "}
                  {product?.stock ?? 0}
                </Typography>
              </StyledBox>
              <StyledBox>
                {skuCoverage !== undefined && skuCoverage !== null && (
                  <Typography variant="title2">{`${intl.formatMessage({
                    id: "productDetail.skuCoverage",
                    defaultMessage: "SKU Coverage:",
                  })} ${skuCoverage.toFixed(1)}`}</Typography>
                )}
              </StyledBox>
            </Box>
            <ProductActionIcons
              product={productDetails || undefined}
              isAnalyticsEnabled={!!isAnalyticsEnabled}
              onRefresh={handleRefreshProduct}
              onAnalytics={handleProductAnalytics}
              onCategories={handleCategories}
              topOffset="16px"
            />
          </StockContainer>

          <ImageContainer>
            {productImage ? (
              <img
                className="image"
                src={productImage}
                alt={product?.name}
                draggable={false}
              />
            ) : (
              <EmptyImageContainer />
            )}
          </ImageContainer>

          <ProductInfoContainer>
            <StyledBox>
              <Tooltip title={product?.name || ""} placement="bottom">
                <ProductName variant="gridTitle">
                  {product?.name || ""}
                </ProductName>
              </Tooltip>
            </StyledBox>
            <StyledBox>
              {code && (
                <Tooltip title={`${code}`} placement="bottom">
                  <ProductDetail variant="title2">{`${code}`}</ProductDetail>
                </Tooltip>
              )}
            </StyledBox>
            <StyledBox>
              {calculatePrice && (
                <Tooltip title={calculatePrice} placement="bottom">
                  <ProductDetail variant="title2">
                    {calculatePrice}
                  </ProductDetail>
                </Tooltip>
              )}
            </StyledBox>
          </ProductInfoContainer>
          {isMultiMetricView && productMetricValues && metricIds ? (
            <MetricContainer>
              <Typography variant="gridTitle">
                {getMultiMetricDisplay(
                  displayMetrics!,
                  metricIds,
                  productMetricValues,
                )}
              </Typography>
            </MetricContainer>
          ) : productMetricValue !== undefined &&
            productMetricValue !== null ? (
            <MetricContainer>
              <Typography variant="gridTitle">{`${productMetricLabel}: ${formatMetricValue(productMetricValue, { type: metricType })}`}</Typography>
            </MetricContainer>
          ) : null}
        </ProductContent>
      </ProductCard>
    </ProductContainer>
  );
};

export default ProductItemComponent;
